import React, { useState } from "react";
import { CoreAbility, AbilityTargetType } from "./abilities";
import { RolePlayer } from "./RoleDistributorReducer";
import { useTranslation } from "react-i18next";

interface Props {
  ability: CoreAbility;
  availablePlayers: RolePlayer[];
  currentPlayerId: number; // 현재 플레이어 ID (자신 구분용)
  seeRolesTargets?: string[]; // 역할 확인 대상 역할 목록
  onTargetSelected: (targetId?: number, choice?: boolean, selectedRole?: string, selectedRoles?: string[], targetIds?: number[]) => void;
  onCancel: () => void;
}

const AbilityTargetSelector: React.FC<Props> = ({
  ability,
  availablePlayers,
  currentPlayerId,
  seeRolesTargets,
  onTargetSelected,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [selectedRolesState, setSelectedRolesState] = useState<string[]>([]);
  const [selectedPlayersState, setSelectedPlayersState] = useState<number[]>([]);

  const handlePlayerSelect = (playerId: number) => {
    onTargetSelected(playerId);
  };

  const handleChoiceSelect = (choice: boolean) => {
    onTargetSelected(undefined, choice);
  };

  const handleRoleToggle = (roleName: string) => {
    setSelectedRolesState(prev =>
      prev.includes(roleName)
        ? prev.filter(r => r !== roleName)
        : [...prev, roleName]
    );
  };

  const handleRoleConfirm = () => {
    if (selectedRolesState.length > 0) {
      onTargetSelected(undefined, undefined, undefined, selectedRolesState);
    }
  };

  const handlePlayerToggle = (playerId: number) => {
    setSelectedPlayersState(prev =>
      prev.includes(playerId)
        ? prev.filter(id => id !== playerId)
        : [...prev, playerId]
    );
  };

  const handlePlayersConfirm = () => {
    if (selectedPlayersState.length > 0) {
      // targetIds를 전달하기 위해 특별한 형태로 전송
      // onTargetSelected의 시그니처를 확장하거나 다른 방법 사용
      // 여기서는 첫 번째 인자로 -1을 전달하고, selectedRoles 대신 플레이어 ID 배열을 전달
      onTargetSelected(undefined, undefined, undefined, undefined, selectedPlayersState);
    }
  };

  const renderContent = () => {
    switch (ability.targetType) {
      case AbilityTargetType.PLAYER:
        return (
          <div className="target-selector-content">
            <h4>
              {ability.id === 'vote' 
                ? t("RoleDistributor.selectVoteTarget", "투표할 대상을 선택하세요") 
                : t("RoleDistributor.selectTarget", "대상을 선택하세요")} - {ability.name} {ability.icon}
            </h4>
            <p className="ability-description">{ability.description}</p>
            
            <div className="player-list">
              {availablePlayers.map((player) => {
                const isMe = player.id === currentPlayerId;
                return (
                  <div
                    key={player.id}
                    className={`player-option ${isMe ? 'is-me' : ''}`}
                    onClick={() => handlePlayerSelect(player.id)}
                  >
                    <div className="player-info">
                      <span className="player-name">
                        {player.name}
                        {isMe && <span className="me-badge"> ({t("RoleDistributor.me", "나")})</span>}
                      </span>
                    </div>
                    <div className="select-arrow">→</div>
                  </div>
                );
              })}
            </div>

            {availablePlayers.length === 0 && (
              <div className="no-players">
                {t("RoleDistributor.noAvailableTargets", "선택 가능한 대상이 없습니다.")}
              </div>
            )}
          </div>
        );

      case AbilityTargetType.CHOICE:
        return (
          <div className="target-selector-content">
            <h4>
              {ability.id === 'secret_vote_choice' 
                ? t("RoleDistributor.makeSecretChoice", "비공개 찬반투표") 
                : t("RoleDistributor.makeChoice", "선택하세요")} - {ability.name} {ability.icon}
            </h4>
            <p className="ability-description">
              {ability.description}
              {ability.id === 'secret_vote_choice' && (
                <>
                  <br />
                  <small>{t("RoleDistributor.secretVoteNote", "개별 선택은 공개되지 않으며, 총계만 공개됩니다.")}</small>
                </>
              )}
            </p>
            
            <div className="choice-options">
              <div
                className="choice-option agree"
                onClick={() => handleChoiceSelect(true)}
              >
                <div className="choice-icon">👍</div>
                <div className="choice-text">
                  {t("RoleDistributor.agree", "찬성")}
                </div>
              </div>
              
              <div
                className="choice-option disagree"
                onClick={() => handleChoiceSelect(false)}
              >
                <div className="choice-icon">👎</div>
                <div className="choice-text">
                  {t("RoleDistributor.disagree", "반대")}
                </div>
              </div>
            </div>
          </div>
        );

      case AbilityTargetType.MULTI_PLAYER:
        return (
          <div className="target-selector-content">
            <h4>
              {t("RoleDistributor.selectTargets", "대상을 선택하세요 (복수 선택 가능)")} - {ability.name} {ability.icon}
            </h4>
            <p className="ability-description">{ability.description}</p>

            <div className="player-list">
              {availablePlayers.map((player) => {
                const isSelected = selectedPlayersState.includes(player.id);
                const isMe = player.id === currentPlayerId;
                return (
                  <div
                    key={player.id}
                    className={`player-option ${isSelected ? 'selected' : ''} ${isMe ? 'is-me' : ''}`}
                    onClick={() => handlePlayerToggle(player.id)}
                  >
                    <div className="player-info">
                      <span className="player-checkbox">
                        {isSelected ? '☑️' : '⬜'}
                      </span>
                      <span className="player-name">
                        {player.name}
                        {isMe && <span className="me-badge"> ({t("RoleDistributor.me", "나")})</span>}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {availablePlayers.length === 0 && (
              <div className="no-players">
                {t("RoleDistributor.noAvailableTargets", "선택 가능한 대상이 없습니다.")}
              </div>
            )}

            {selectedPlayersState.length > 0 && (
              <div className="selected-roles-summary">
                <span className="summary-label">
                  {t("RoleDistributor.selectedCount", "선택됨")}: {selectedPlayersState.length}{t("RoleDistributor.playerUnit", "명")}
                </span>
              </div>
            )}
          </div>
        );

      case AbilityTargetType.ROLE:
        return (
          <div className="target-selector-content">
            <h4>
              {t("RoleDistributor.selectRolesToSee", "확인할 역할을 선택하세요 (복수 선택 가능)")} - {ability.name} {ability.icon}
            </h4>
            <p className="ability-description">{ability.description}</p>

            <div className="role-list">
              {seeRolesTargets && seeRolesTargets.length > 0 ? (
                seeRolesTargets.map((roleName) => {
                  const isSelected = selectedRolesState.includes(roleName);
                  return (
                    <div
                      key={roleName}
                      className={`role-option ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleRoleToggle(roleName)}
                    >
                      <div className="role-info">
                        <span className="role-checkbox">
                          {isSelected ? '☑️' : '⬜'}
                        </span>
                        <span className="role-name">{roleName}</span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="no-roles">
                  {t("RoleDistributor.noRolesToSee", "확인 가능한 역할이 없습니다.")}
                </div>
              )}
            </div>

            {selectedRolesState.length > 0 && (
              <div className="selected-roles-summary">
                <span className="summary-label">
                  {t("RoleDistributor.selectedRoles", "선택됨")}: {selectedRolesState.join(", ")}
                </span>
              </div>
            )}

          </div>
        );

      default:
        return (
          <div className="target-selector-content">
            <h4>{ability.name} {ability.icon}</h4>
            <p>{ability.description}</p>
            <button onClick={() => onTargetSelected()}>
              {t("RoleDistributor.confirm", "확인")}
            </button>
          </div>
        );
    }
  };

  return (
    <div className="ability-target-selector-overlay">
      <div className="ability-target-selector-modal">
        {renderContent()}
        
        <div className="modal-actions">
          <button className="btn-cancel" onClick={onCancel}>
            {t("RoleDistributor.cancel", "취소")}
          </button>
          {ability.targetType === AbilityTargetType.MULTI_PLAYER && (
            <button
              className="btn-cancel"
              onClick={handlePlayersConfirm}
              disabled={selectedPlayersState.length === 0}
            >
              {t("RoleDistributor.confirmSelection", "확인하기")}
            </button>
          )}
          {ability.targetType === AbilityTargetType.ROLE && (
            <button
              className="btn-cancel"
              onClick={handleRoleConfirm}
              disabled={selectedRolesState.length === 0}
            >
              {t("RoleDistributor.confirmSelection", "확인하기")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AbilityTargetSelector;